import * as React from 'react'
import DromoUploader, {
  IColumnHookInput,
  IColumnHookOutput,
  IColumnHook,
  IDeveloperField,
  IValidatorField,
  IDeveloperSettings,
  IRowHookInput,
  IRowHookOutput,
  IRowHook,
  IDeveloperStyleOverrides,
  IUser,
  IResultMetadata,
  IStepHook,
  IRowDeleteHook,
  IBeforeFinishCallback,
  IBulkRowHook,
  IImporterOptions,
  IParseFileCallback
} from 'dromo-uploader-js'

type VerboseRequiredProps = {
  user: IUser
  fields: IDeveloperField[]
  licenseKey: string
  settings: IDeveloperSettings
}

type SavedSchemaProps = {
  licenseKey: string
} & ({ schemaName: string } | { schemaId: string }) &
  IImporterOptions

type CommonProps = {
  style?: React.CSSProperties
  className?: string
  rowHooks?: IRowHook[]
  bulkRowHooks?: IBulkRowHook[]
  rowDeleteHooks?: IRowDeleteHook[]
  columnHooks?: IColumnHook[]
  stepHooks?: IStepHook[]
  fileParsers?: { extensions: string[]; parseFile: IParseFileCallback }[]
  beforeFinish?: IBeforeFinishCallback
  onResults?: (data: any, metadata: IResultMetadata) => void
  onCancel?: () => void
  children?: React.ReactNode
  open?: boolean
  inline?: boolean
}

type Props = (VerboseRequiredProps | SavedSchemaProps) & CommonProps

export default class DromoReactUploader extends React.Component<Props> {
  dromo: any
  inlineRef: React.RefObject<HTMLDivElement> | null = null

  constructor(props: Props) {
    super(props)

    if (props.inline) {
      this.inlineRef = React.createRef()
    }
  }

  init = () => {
    if ('fields' in this.props && 'settings' in this.props) {
      this.dromo = new DromoUploader(
        this.props.licenseKey,
        this.props.fields,
        this.props.settings,
        this.props.user
      )
    } else if ('schemaId' in this.props || 'schemaName' in this.props) {
      const idOrName =
        'schemaId' in this.props ? this.props.schemaId : this.props.schemaName

      this.dromo = new DromoUploader(this.props.licenseKey, idOrName)

      if ('developmentMode' in this.props) {
        this.dromo.setDevelopmentMode(this.props.developmentMode)
      }
      if ('user' in this.props) {
        this.dromo.setUser(this.props.user)
      }
      if ('headerRowOverride' in this.props) {
        this.dromo.setHeaderRowOverride(this.props.headerRowOverride)
      }
    }

    if (this.props.rowHooks) {
      this.props.rowHooks.forEach((hook) => this.dromo.registerRowHook(hook))
    }

    if (this.props.bulkRowHooks) {
      this.props.bulkRowHooks.forEach((hook) =>
        this.dromo.registerBulkRowHook(hook)
      )
    }

    if (this.props.columnHooks) {
      this.props.columnHooks.forEach((hook) => {
        this.dromo.registerColumnHook(hook.fieldName, hook.callback)
      })
    }

    if (this.props.rowDeleteHooks) {
      this.props.rowDeleteHooks.forEach((hook) => {
        return this.dromo.registerRowDeleteHook(hook)
      })
    }

    if (this.props.beforeFinish) {
      this.dromo.beforeFinish(this.props.beforeFinish)
    }

    if (this.props.onResults) {
      this.dromo.onResults(this.props.onResults)
    }

    if (this.props.onCancel) {
      this.dromo.onCancel(this.props.onCancel)
    }

    if (this.props.stepHooks) {
      this.props.stepHooks.forEach((hook) => {
        this.dromo.registerStepHook(hook.type, hook.callback)
      })
    }

    if (this.props.fileParsers) {
      this.props.fileParsers.forEach((parser) => {
        this.dromo.registerFileParser(parser)
      })
    }
  }

  open = () => {
    this.init()
    this.dromo.open()
  }

  initInline = () => {
    if (!this.inlineRef) return

    this.init()
    this.dromo.initInline(this.inlineRef.current)
  }

  componentDidMount() {
    if (this.props.inline && this.inlineRef) {
      this.initInline()
      return
    }

    if (this.props.open) this.open()
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.inline) {
      this.initInline()
      return
    }

    if (!prevProps.open && this.props.open) {
      this.open()
    }

    if (prevProps.open && !this.props.open) {
      this.dromo.close()
    }
  }

  componentWillUnmount() {
    if (this.props.open && !this.props.inline) {
      this.dromo.close()
    }
  }

  render() {
    if (this.props.inline) {
      return (
        <div
          ref={this.inlineRef}
          className={this.props.className}
          style={this.props.style}
        />
      )
    }

    if (this.props.children === undefined) return <React.Fragment />

    return (
      <button
        style={this.props.style}
        className={this.props.className}
        onClick={this.open}
      >
        {this.props.children}
      </button>
    )
  }
}

export {
  IDeveloperField,
  IDeveloperSettings,
  IUser,
  IColumnHookInput,
  IColumnHookOutput,
  IColumnHook,
  IRowHookInput,
  IRowHookOutput,
  IRowHook,
  IValidatorField,
  IDeveloperStyleOverrides,
  IResultMetadata,
  IStepHook,
  IRowDeleteHook,
  IBeforeFinishCallback,
  IBulkRowHook,
  IImporterOptions,
  IParseFileCallback
}
